import { Globe } from "lucide-react";
import { getRegionCountryCode } from "@/data/citation-styles";
import { cn } from "@/lib/utils";

type RegionFlagSize = "sm" | "md" | "lg" | "xl";

interface RegionFlagProps {
  region: string | undefined;
  className?: string;
  size?: RegionFlagSize;
  /** Accessible name for the globe shown instead of a flag. Defaults to English. */
  internationalLabel?: string;
}

/**
 * Rendered box per size. Flags are 3:2, so the height is the width divided by
 * 1.5; both are set as attributes so the row never reflows while the SVG loads.
 */
const FLAG_BOX: Record<RegionFlagSize, { width: number; height: number }> = {
  sm: { width: 18, height: 12 },
  md: { width: 21, height: 14 },
  lg: { width: 27, height: 18 },
  /** Page headings, where the flag sits beside 3xl/4xl type. */
  xl: { width: 42, height: 28 },
};

const iconSizes: Record<RegionFlagSize, string> = {
  sm: "size-3.5",
  md: "size-4",
  lg: "size-5",
  xl: "size-8",
};

/**
 * Country flag for a style's region, or a Globe icon for International.
 *
 * Flags are vendored SVGs under `public/flags/`, not emoji. Windows has no flag
 * glyphs in Segoe UI Emoji, so Chrome and Edge on Windows draw a
 * regional-indicator pair (U+1F1FA U+1F1F8) as the two bare letters "us" — which
 * is what a Windows user saw next to "APA 7". An `<img>` renders identically on
 * every platform and keeps the artwork out of the JS bundle. Regenerate the files
 * with `corepack pnpm generate:flags` after editing `REGION_TO_COUNTRY`.
 */
export function RegionFlag({
  region,
  className,
  size = "md",
  internationalLabel = "International",
}: RegionFlagProps) {
  const code = getRegionCountryCode(region);
  if (code) {
    const { width, height } = FLAG_BOX[size];
    return (
      // biome-ignore lint/performance/noImgElement: next/image cannot optimize SVG without dangerouslyAllowSVG, and would add a wrapper element per row in lists that render hundreds of flags
      <img
        alt=""
        aria-hidden
        className={cn(
          // The hairline keeps flags with white edges (Japan, Poland) from
          // dissolving into a light card background.
          "inline-block shrink-0 rounded-[2px] object-cover ring-1 ring-black/10 dark:ring-white/20",
          className
        )}
        decoding="async"
        height={height}
        loading="lazy"
        src={`/flags/${code}.svg`}
        width={width}
      />
    );
  }
  return (
    <Globe
      aria-label={internationalLabel}
      className={cn(
        "inline-block shrink-0 text-muted-foreground",
        iconSizes[size],
        className
      )}
    />
  );
}
